import {
  Header,
  Sidebar,
  SidebarFooter,
  SidebarItem,
  SidebarTab,
  SidebarTabs,
  SidebarTabLabel,
  SidebarPageWrapper,
} from '@trueprodigy/components';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import React, { useState, useEffect, useContext } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import * as globalActions from '@actions/global';
import * as globalAppraisalActions from '@modules/appraisal/actions/global';
import * as searchBiActions from '@modules/bi/actions/search';
import {
  isSidebarVisibleSelector,
  userStatusSelector,
  userRightsSelector,
} from '@selectors/global';
import SidebarAccountSettingsWidget from '@components/Sidebar/SidebarAccountSettingsWidget';
import SidebarCaseSettingWidget from '@components/Sidebar/SidebarCaseSettingWidget';
import SidebarRdsNameWidget from '@components/Sidebar/SidebarRdsNameWidget';
import rocketIcon from '@components/Sidebar/assets/rocket-icon.png';

import { activeReportSelector } from '@modules/bi/selectors/search';

import biLogo from '@assets/images/true-prodigy-logo-BI.svg';
import { parseHeaderTitle, prefix } from '../routes';
import { canEditSubject } from '@config/permissions/Can';
import ability, { defineRules } from '@config/permissions/ability';

const PageWrapper = ({
  activeReport,
  children,
  location,
  toggleSidebar,
  isSidebarVisible,
  fetchAppraisalYears,
  resetActiveReport,
  history,
  userStatus,
  userRights,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const activeID = activeReport.data && activeReport.data.reportID;
  const isReadOnly = userStatus.get('readOnly');
  const theme = useContext(ThemeContext);

  useEffect(() => {
    fetchYearData();
  }, []);

  useEffect(() => {
    if (!isNaN(activeID)) {
      setActiveTab(1);
    }
  }, [activeID]);

  useEffect(() => {
    if (userRights && userRights.toJS() && userRights.toJS().length) {
      const rules = defineRules(userRights.toJS());
      ability.update(rules);
    }
  }, [userRights]);

  useEffect(() => {
    const isiPad = navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints;
    if (isSidebarVisible && isiPad) {
      toggleSidebar();
    }
  }, [location.pathname]);

  const fetchYearData = async () => {
    await fetchAppraisalYears();
  };

  return (
    <>
      <Helmet>
        <title>Prodigy BI</title>
      </Helmet>

      <Sidebar
        isSidebarVisible={isSidebarVisible}
        logo={biLogo}
        sidebarFooter={
          <SidebarFooter>
            <SidebarRdsNameWidget />
            <SidebarCaseSettingWidget />
            <SidebarAccountSettingsWidget />
          </SidebarFooter>
        }
      >
        <SidebarItem icon={rocketIcon} link="/" title="Prodigy App Launcher" />
        <SidebarTabs
          indicatorColor="secondary"
          scrollButtons="off"
          textColor="primary"
          value={activeTab}
          variant="fullWidth"
          onChange={(_e, i) => {
            setActiveTab(i);
          }}
        >
          <SidebarTab
            label={<SidebarTabLabel isActive={activeTab === 0} label="BI" />}
          />
          {activeReport.data && activeReport.data.reportID && (
            <SidebarTab
              label={
                <SidebarTabLabel
                  hasClose
                  isActive={activeTab === 1}
                  label="Report"
                  onClose={(event) => {
                    event.stopPropagation();
                    resetActiveReport();
                    setActiveTab(0);
                    history.push('/bi/search');
                  }}
                />
              }
            />
          )}
        </SidebarTabs>
        {activeTab === 0 ? (
          <>
            <SidebarItem link={prefix + '/search'} title="Reports" />
            <SidebarItem
              link={prefix + '/smart-lists/search'}
              title="Smart Lists"
            />
            <SidebarItem nested title="BradSQL">
              <SidebarItem
                link={`${prefix}/bradsql/create-query`}
                title="Create New Query"
              />
              <SidebarItem
                link={`${prefix}/bradsql/query-repository`}
                title="Query Repository"
              />
            </SidebarItem>
            {isReadOnly ? (
              <></>
            ) : (
              <SidebarItem nested title="Data Validation">
                <SidebarItem
                  link={`/bi/data-validation/gain-loss-analysis`}
                  title="Gain/Loss Analysis"
                />
                <SidebarItem
                  link={`/bi/data-validation/data-validation-lists`}
                  title="Data Validation Lists"
                />
              </SidebarItem>
            )}
            <SidebarItem nested title="System Reports">
              <SidebarItem
                link={`/bi/system-reports/merged-property`}
                title="Merged Property"
              />
              <SidebarItem
                link={`/bi/system-reports/split-property`}
                title="Split Property"
              />
              <SidebarItem
                link={`/bi/system-reports/newly-created-property`}
                title="Newly Created Property"
              />
              <SidebarItem
                link={`/bi/system-reports/income-worksheets`}
                title="Income Worksheets"
              />
              <SidebarItem
                link={`/bi/system-reports/operations-survey`}
                title="Operations Survey"
              />
              <SidebarItem
                link={`/bi/system-reports/new-subdivision-list`}
                title="New Subdivision List"
              />
              <SidebarItem
                link={`/bi/system-reports/improvement-pricing-schedules`}
                title="Improvement Pricing Schedules"
              />
              <SidebarItem
                link={`/bi/system-reports/land-pricing-schedules`}
                title="Land Pricing Schedules"
              />
            </SidebarItem>
          </>
        ) : null}
        {activeTab === 1 && activeReport.data && activeReport.data.reportID ? (
          <>
            <SidebarItem
              link={`/bi/edit/${activeReport.data.reportID}`}
              title="Edit"
            />
            <SidebarItem
              link={`/bi/search/report/${activeReport.data.reportID}`}
              title="Run"
            />
          </>
        ) : null}
        {canEditSubject('Dashboard Admin') ? (
          <SidebarItem
            link={prefix + '/dashboard-config'}
            title="Appraisal Dashboard Configuration"
          />
        ) : null}
        {/* <SidebarItem isOpen nested title="Reports">
          <SidebarItem
            key={prefix + '/reports/appraisal-roll/preliminary-totals'}
            link={prefix + '/reports/appraisal-roll/preliminary-totals'}
            title={'Preliminary Totals'}
          />
          <SidebarItem
            key={prefix + '/reports/appraisal-roll/preliminary-totals'}
            link={prefix + '/reports/appraisal-roll/preliminary-totals'}
            title={'Preliminary Totals'}
          />
          <SidebarItem
            isOpen
            nested
            key={prefix + '/reports/appraisal-roll'}
            link={prefix + '/reports/appraisal-roll'}
            title={'Appraisal Roll'}
          >
            <SidebarItem
              key={prefix + '/reports/appraisal-roll/preliminary-totals'}
              link={prefix + '/reports/appraisal-roll/preliminary-totals'}
              title={'Preliminary Totals'}
            />
            <SidebarItem
              key={prefix + '/reports/appraisal-roll/certified-totals'}
              link={prefix + '/reports/appraisal-roll/certified-totals'}
              title={'Certified Totals'}
            />
          </SidebarItem>
        </SidebarItem> */}
        {/* <SidebarItem link={prefix + '/saved-reports'} title="Saved Reports" /> */}
      </Sidebar>
      <PageWrapper.ContentWrapper>
        <SidebarPageWrapper
          isSidebarVisible={isSidebarVisible}
          className="test"
          style={{ width: '100%' }}
        >
          <Header
            title={parseHeaderTitle(location.pathname)}
            toggleSidebar={toggleSidebar}
            imgSrc={theme.header?.imgSrc}
          />
          <div>{children}</div>
        </SidebarPageWrapper>
      </PageWrapper.ContentWrapper>
    </>
  );
};

PageWrapper.propTypes = {
  activeReport: PropTypes.object,
  children: PropTypes.any,
  location: PropTypes.any,
  toggleSidebar: PropTypes.func,
  isSidebarVisible: PropTypes.bool,
  fetchAppraisalYears: PropTypes.func.isRequired,
  resetActiveReport: PropTypes.func.isRequired,
  history: PropTypes.any,
  userStatus: PropTypes.any,
  userRights: PropTypes.any,
};

PageWrapper.ContentWrapper = styled.div`
  > div {
    margin-top: -67px;
    min-height: 100vh;
    background-color: #f2f4f6;
  }
`;

PageWrapper.MenuTitle = styled.div`
  height: 35px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.4);
  background-color: #fff;
  color: #2f3e9e;
  font-size: 14px;
  padding: 8px 26px;
  font-weight: normal;
`;

const mapStateToProps = (state) => ({
  userRights: userRightsSelector(state),
  activeReport: activeReportSelector(state),
  isSidebarVisible: isSidebarVisibleSelector(state),
  userStatus: userStatusSelector(state),
});

const mapDispatchToProps = {
  toggleSidebar: globalActions.toggleSidebar,
  fetchAppraisalYears: globalAppraisalActions.fetchAppraisalYears,
  resetActiveReport: searchBiActions.resetActiveReport,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PageWrapper),
);
